import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { zynity_api } from '../../../../../config/external-routes';
import { OrgStatementTinyMceField } from '../../../../admin/organizations/details/OrgStatementTinyMceField';
import { DocumentLibraryPickerItem, DocumentLibraryPickerModal } from '../../../../ui/tools/DocumentLibraryPickerModal';
import {
    fetchLibraryContextItems,
    syncLibraryContextItems,
    registerLinkInLibrary,
    registerUploadInLibrary,
    STRATEGY_ITEM_CONTEXT_TYPE,
} from './libraryContext';

type Link = { id: number; links: string };
type Upload = { id: number; fileName: string };
type Comment = {
    id: number;
    comment: string;
    idAuthor: number;
    dateCreated: string | null;
    idStrategiesComments: number | null;
};

type Props = {
    organizationId: string;
    teamId: string;
    categoryId: number;
    allowEdit: boolean;
    currentUserId?: number;
};

/**
 * Comments/Links/Uploads/Library panel for a Category or Discussion topic (Phase 6 +
 * 7, §10, comments added later once `StrategyComments.idStrategies` was confirmed —
 * via a real `DESCRIBE` against the table, not just the docblock guess an earlier
 * pass relied on — to be a genuinely working Yii comment target for categories/
 * topics, matching `StrategyComment::insertCommentsDatum()`'s `$strategyId` param).
 * Links/Uploads are scoped via `idStrategies` + `type='Category'` (see
 * StrategyLink::scopeForCategory()'s docblock), a different convention than Details/
 * Milestone/Next-Step, which is why this isn't just CommentsAndAttachments with
 * `entityParam` extended to a `category_id` case — but Comments themselves reuse the
 * same avatar/name/date presentation as CommentsAndAttachments.tsx for consistency.
 * Library uses the generic `strategy_item` document-library context (Phase 7) —
 * same convention as Yii's own `buildStrategyItemDocumentContext()`.
 */
export const CategoryAttachments = ({ organizationId, teamId, categoryId, allowEdit, currentUserId }: Props) => {
    const [links, setLinks] = useState<Link[]>([]);
    const [uploads, setUploads] = useState<Upload[]>([]);
    const [libraryItems, setLibraryItems] = useState<DocumentLibraryPickerItem[]>([]);
    const [showLibraryPicker, setShowLibraryPicker] = useState(false);
    const [syncingLibrary, setSyncingLibrary] = useState(false);
    const [loading, setLoading] = useState(true);
    const [newLinkUrl, setNewLinkUrl] = useState('');
    const [posting, setPosting] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [postingComment, setPostingComment] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
    const [editingCommentText, setEditingCommentText] = useState('');
    const [replyingToId, setReplyingToId] = useState<number | null>(null);
    const [replyText, setReplyText] = useState('');

    const organizationTeam = useSelector((state: any) => state.organization_team);
    const teamMembers: any[] = useMemo(() => organizationTeam?.data?.members ?? [], [organizationTeam?.data?.members]);
    const memberId = (member: any) => (member?.People_id !== undefined && member?.People_id !== null ? member.People_id : member?.id);
    const getAuthor = (authorId: number) => teamMembers.find((m: any) => Number(memberId(m)) === Number(authorId));
    const getAuthorInitials = (authorId: number) => {
        const author = getAuthor(authorId);
        if (!author) {
            return '--';
        }
        if (!author.FirstName) {
            return (author.EmailAddress ?? '').substring(0, 2).toUpperCase();
        }
        return `${author.FirstName.substring(0, 1)}${(author.LastName ?? '').substring(0, 1)}`.toUpperCase();
    };
    const getAuthorFirstName = (authorId: number) => {
        const author = getAuthor(authorId);
        return author?.FirstName || author?.KnownAs || author?.EmailAddress || 'Unknown';
    };
    const formatCommentDate = (dateCreated: string | null) => {
        if (!dateCreated) {
            return '';
        }
        return new Date(dateCreated).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const commentsUrl = zynity_api.organization_team_strategies_category_comments
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{category_id}', String(categoryId));

    const commentItemUrl = (commentId: number) =>
        zynity_api.organization_team_strategies_category_comment_item
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId)
            .replace('{category_id}', String(categoryId))
            .replace('{comment_id}', String(commentId));

    const attachmentsUrl = zynity_api.organization_team_strategies_category_attachments
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{category_id}', String(categoryId));

    const linksUrl = zynity_api.organization_team_strategies_category_attachment_links
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{category_id}', String(categoryId));

    const linkItemUrl = (linkId: number) =>
        zynity_api.organization_team_strategies_category_attachment_link_item
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId)
            .replace('{link_id}', String(linkId));

    const uploadsUrl = zynity_api.organization_team_strategies_category_attachment_uploads
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{category_id}', String(categoryId));

    const uploadItemUrl = (uploadId: number) =>
        zynity_api.organization_team_strategies_category_attachment_upload_item
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId)
            .replace('{upload_id}', String(uploadId));

    const fetchAll = () => {
        setLoading(true);
        Promise.all([
            axios.get(attachmentsUrl),
            fetchLibraryContextItems(organizationId, teamId, STRATEGY_ITEM_CONTEXT_TYPE, categoryId),
            axios.get(commentsUrl),
        ])
            .then(([attachmentsResponse, libraryDocs, commentsResponse]) => {
                setLinks(attachmentsResponse.data.links ?? []);
                setUploads(attachmentsResponse.data.uploads ?? []);
                setLibraryItems(libraryDocs);
                setComments(commentsResponse.data.comments ?? []);
            })
            .catch(() => {
                setLinks([]);
                setUploads([]);
                setLibraryItems([]);
                setComments([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    const syncLibrary = async (nextItems: DocumentLibraryPickerItem[]) => {
        setSyncingLibrary(true);
        try {
            await syncLibraryContextItems(
                organizationId,
                teamId,
                STRATEGY_ITEM_CONTEXT_TYPE,
                categoryId,
                `Strategies / Category: ${categoryId}`,
                nextItems.map((item) => item.id)
            );
            setLibraryItems(nextItems);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'The Library selection could not be updated.' });
        } finally {
            setSyncingLibrary(false);
        }
    };

    const handleChooseLibraryItem = (item: DocumentLibraryPickerItem) => {
        if (libraryItems.some((existing) => existing.id === item.id)) {
            setShowLibraryPicker(false);
            return;
        }
        syncLibrary([...libraryItems, item]);
        setShowLibraryPicker(false);
    };

    const removeLibraryItem = (itemId: number) => {
        syncLibrary(libraryItems.filter((item) => item.id !== itemId));
    };

    // Shadow-indexing (matches Yii's DocumentLibraryBridge::syncContext(), confirmed
    // Phase 7 §10 addendum 2026-08-27): every raw link/upload added here also becomes
    // a reusable Document Library item, so it's findable from any other "Choose from
    // Library" picker afterward — not just this category's own list. Best-effort: a
    // failure here doesn't roll back the link/upload itself, which already saved.
    const mergeIntoLibrary = async (newItem: DocumentLibraryPickerItem) => {
        try {
            const nextIds = [...libraryItems.map((item) => item.id), newItem.id];
            await syncLibraryContextItems(
                organizationId,
                teamId,
                STRATEGY_ITEM_CONTEXT_TYPE,
                categoryId,
                `Strategies / Category: ${categoryId}`,
                nextIds
            );
        } catch (error) {
            // Best-effort — see comment above.
        }
    };

    const handleAddLink = async () => {
        if (newLinkUrl.trim() === '') {
            return;
        }
        setPosting(true);
        try {
            const url = newLinkUrl.trim();
            await axios.post(linksUrl, { url });
            try {
                const libraryItem = await registerLinkInLibrary(organizationId, teamId, url);
                await mergeIntoLibrary(libraryItem);
            } catch (error) {
                // Best-effort — see mergeIntoLibrary's comment.
            }
            setNewLinkUrl('');
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This link could not be added.' });
        } finally {
            setPosting(false);
        }
    };

    const removeLink = async (link: Link) => {
        try {
            await axios.delete(linkItemUrl(link.id));
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This link could not be removed.' });
        }
    };

    const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('storage_name', `${Date.now()}_${categoryId}_${Math.random().toString(36).slice(2, 8)}_${file.name}`);
            const tempResponse = await axios.post(zynity_api.global_upload_temp_files, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            await axios.post(uploadsUrl, { file_name: tempResponse.data.file_name });
            try {
                const libraryItem = await registerUploadInLibrary(organizationId, teamId, file, file.name);
                await mergeIntoLibrary(libraryItem);
            } catch (error) {
                // Best-effort — see mergeIntoLibrary's comment.
            }
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Upload failed', text: 'This file could not be uploaded.' });
        } finally {
            setUploading(false);
        }
    };

    const removeUpload = async (upload: Upload) => {
        try {
            await axios.delete(uploadItemUrl(upload.id));
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This file could not be removed.' });
        }
    };

    const postComment = async () => {
        if (newComment.trim() === '') {
            return;
        }
        setPostingComment(true);
        try {
            await axios.post(commentsUrl, { comment: newComment });
            setNewComment('');
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This comment could not be posted.' });
        } finally {
            setPostingComment(false);
        }
    };

    const postReply = async (parentId: number) => {
        if (replyText.trim() === '') {
            return;
        }
        try {
            await axios.post(commentsUrl, { comment: replyText, idStrategiesComments: parentId });
            setReplyingToId(null);
            setReplyText('');
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This reply could not be posted.' });
        }
    };

    const startEditComment = (comment: Comment) => {
        setEditingCommentId(comment.id);
        setEditingCommentText(comment.comment);
    };

    const saveEditComment = async () => {
        if (editingCommentId === null) {
            return;
        }
        try {
            await axios.put(commentItemUrl(editingCommentId), { comment: editingCommentText });
            setEditingCommentId(null);
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This comment could not be updated.' });
        }
    };

    const deleteComment = async (comment: Comment) => {
        const result = await Swal.fire({
            title: 'Delete this comment?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.delete(commentItemUrl(comment.id));
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This comment could not be deleted.' });
        }
    };

    const topLevelComments = comments.filter((comment) => !comment.idStrategiesComments);
    const repliesFor = (parentId: number) => comments.filter((comment) => comment.idStrategiesComments === parentId);

    const renderCommentRow = (comment: Comment, isReply: boolean) => (
        <div
            key={comment.id}
            className={`border rounded p-2${isReply ? ' ws-v1-detail-comment--reply ms-4 mt-2' : ''}`}
        >
            {editingCommentId === comment.id ? (
                <div>
                    <OrgStatementTinyMceField
                        id={`edit-category-comment-${comment.id}`}
                        name="editingComment"
                        height={120}
                        value={editingCommentText}
                        onChange={(_name, value) => setEditingCommentText(value)}
                    />
                    <div className="d-flex gap-2 mt-2">
                        <Button size="sm" className="btn-teal" onClick={saveEditComment}>
                            Save
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setEditingCommentId(null)}>
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="d-flex gap-2">
                    <span className="strategies-comment-avatar" title={getAuthorFirstName(comment.idAuthor)}>
                        {getAuthorInitials(comment.idAuthor)}
                    </span>
                    <div className="flex-grow-1 d-flex justify-content-between align-items-start">
                        <div>
                            <div className="small">
                                <span className="strategies-comment-author-name">{getAuthorFirstName(comment.idAuthor)}</span>{' '}
                                <span className="strategies-comment-date">{formatCommentDate(comment.dateCreated)}</span>
                            </div>
                            <div className="small" dangerouslySetInnerHTML={{ __html: comment.comment }} />
                        </div>
                        <div className="d-flex gap-1">
                            {!isReply && allowEdit && (
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setReplyingToId(comment.id);
                                        setReplyText('');
                                    }}
                                >
                                    Reply
                                </Button>
                            )}
                            {currentUserId === comment.idAuthor && (
                                <Button size="sm" variant="outline-secondary" onClick={() => startEditComment(comment)}>
                                    Edit
                                </Button>
                            )}
                            {allowEdit && (
                                <Button size="sm" variant="outline-danger" onClick={() => deleteComment(comment)}>
                                    Delete
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const displayFileName = (fileName: string) => {
        const parts = fileName.split('_');
        return parts.length > 3 ? parts.slice(3).join('_') : fileName;
    };

    if (loading) {
        return <p className="text-muted small">Loading...</p>;
    }

    return (
        <div className="strategies-comments-and-attachments mt-2">
            <div className="mb-3" data-testid="parent-library-section">
                <div className="ws-v1-section-label small text-muted mb-1">Library</div>
                <div className="small text-muted mb-2">Select existing library-only documents for this record.</div>
                {libraryItems.length > 0 && (
                    <ul className="ws-v1-detail-library-list small">
                        {libraryItems.map((item) => (
                            <li key={item.id} className="d-flex justify-content-between align-items-center">
                                <span>{item.title}</span>
                                {allowEdit && (
                                    <Button
                                        size="sm"
                                        variant="outline-danger"
                                        disabled={syncingLibrary}
                                        onClick={() => removeLibraryItem(item.id)}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {libraryItems.length === 0 && <div className="text-muted small mb-2">No library-only documents selected.</div>}
                {allowEdit && (
                    <Button size="sm" className="btn-teal" disabled={syncingLibrary} onClick={() => setShowLibraryPicker(true)}>
                        Choose from Library
                    </Button>
                )}
                <DocumentLibraryPickerModal
                    show={showLibraryPicker}
                    onHide={() => setShowLibraryPicker(false)}
                    onSelectItem={handleChooseLibraryItem}
                    selectedItemIds={libraryItems.map((item) => item.id)}
                />
            </div>

            <div className="mb-3">
                <div className="ws-v1-section-label small text-muted mb-1">Links</div>
                {links.length > 0 && (
                    <ul className="ws-v1-detail-links-list small">
                        {links.map((link) => (
                            <li key={link.id} className="d-flex justify-content-between align-items-center">
                                <a href={link.links} target="_blank" rel="noopener noreferrer">
                                    {link.links}
                                </a>
                                {allowEdit && (
                                    <Button size="sm" variant="outline-danger" onClick={() => removeLink(link)}>
                                        Remove
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {allowEdit && (
                    <div className="d-flex gap-2">
                        <Form.Control
                            size="sm"
                            placeholder="https://..."
                            value={newLinkUrl}
                            onChange={(e) => setNewLinkUrl(e.target.value)}
                        />
                        <Button size="sm" className="btn-teal" disabled={posting || newLinkUrl.trim() === ''} onClick={handleAddLink}>
                            Add Link
                        </Button>
                    </div>
                )}
            </div>

            <div>
                <div className="ws-v1-section-label small text-muted mb-1">Uploads</div>
                {uploads.length > 0 && (
                    <ul className="ws-v1-detail-uploads-list small">
                        {uploads.map((upload) => (
                            <li key={upload.id} className="d-flex justify-content-between align-items-center">
                                <a href={zynity_api.download_file + upload.fileName} target="_blank" rel="noopener noreferrer">
                                    {displayFileName(upload.fileName)}
                                </a>
                                {allowEdit && (
                                    <Button size="sm" variant="outline-danger" onClick={() => removeUpload(upload)}>
                                        Remove
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {allowEdit && (
                    <Form.Control size="sm" type="file" disabled={uploading} onChange={handleUploadFile} />
                )}
            </div>

            <div className="mt-3">
                <div className="ws-v1-section-label small text-muted mb-1">Comments</div>
                {topLevelComments.length === 0 ? (
                    <p className="text-muted small">No comments yet.</p>
                ) : (
                    <div className="d-flex flex-column gap-2 mb-2">
                        {topLevelComments.map((comment) => (
                            <div key={comment.id}>
                                {renderCommentRow(comment, false)}
                                {repliesFor(comment.id).map((reply) => renderCommentRow(reply, true))}
                                {replyingToId === comment.id && (
                                    <div className="ms-4 mt-2">
                                        <OrgStatementTinyMceField
                                            id={`reply-category-comment-${comment.id}`}
                                            name="replyComment"
                                            height={100}
                                            value={replyText}
                                            onChange={(_name, value) => setReplyText(value)}
                                        />
                                        <div className="d-flex gap-2 mt-2">
                                            <Button size="sm" className="btn-teal" onClick={() => postReply(comment.id)}>
                                                Post Reply
                                            </Button>
                                            <Button size="sm" variant="outline-secondary" onClick={() => setReplyingToId(null)}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {allowEdit && (
                    <div>
                        <OrgStatementTinyMceField
                            id="new-category-comment"
                            name="newComment"
                            height={100}
                            value={newComment}
                            onChange={(_name, value) => setNewComment(value)}
                        />
                        <Button
                            size="sm"
                            className="btn-teal mt-2"
                            disabled={postingComment || newComment.trim() === ''}
                            onClick={postComment}
                        >
                            Post
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
